/**
 * @typedef {Object} Product
 * @property {string} slug
 * @property {string} name
 * @property {string} category
 * @property {string} shortDescription
 * @property {string} description
 * @property {string[]} problems
 * @property {string[]} features
 * @property {string | null} image
 * @property {string} imageAlt
 * @property {string} route
 * @property {string} legacyRoute
 * @property {string} accent
 * @property {string} seoDescription
 * @property {string} seoTitle
 * @property {string} directAnswer
 * @property {string} applicationSubCategory
 * @property {string[]} operatingSystems
 * @property {string} idealFor
 * @property {{question: string, answer: string, answerHtml?: string}[]} faq
 */

/** @type {Product[]} */
export const products = [
  {
    slug: "innoadmin",
    name: "InnoAdmin",
    category: "Administración y finanzas",
    shortDescription: "Centraliza la operación administrativa y financiera de tu empresa.",
    description: "Sistema administrativo y financiero para gestionar operaciones, facturación, compras, gastos y procesos contables desde un mismo entorno.",
    problems: [
      "Información administrativa dispersa",
      "Procesos de compras y gastos difíciles de rastrear",
      "Poca visibilidad sobre facturación y operación",
    ],
    features: ["Facturación y cotizaciones", "Compras y proveedores", "Registro de gastos", "Gestión de clientes", "Reportes administrativos", "Procesos contables"],
    image: "/assets/products/innoadmin-dashboard.png",
    imageAlt: "Dashboard de InnoAdmin con indicadores administrativos y financieros",
    route: "/innoadmin",
    legacyRoute: "/productos/innoadmin",
    accent: "sky",
    seoDescription: "InnoAdmin es un software administrativo y financiero para facturación, compras, gastos, clientes y procesos contables en República Dominicana.",
    seoTitle: "InnoAdmin | Software administrativo y financiero",
    directAnswer: "InnoAdmin es un sistema administrativo y financiero que centraliza facturación, compras, gastos, clientes y procesos contables en un solo entorno.",
    applicationSubCategory: "Software administrativo y financiero",
    operatingSystems: ["Web"],
    idealFor: "Empresas que necesitan centralizar y dar seguimiento a su operación administrativa y financiera.",
    faq: [
      { question: "¿Qué procesos gestiona InnoAdmin?", answer: "Gestiona facturación y cotizaciones, compras, proveedores, gastos, clientes, reportes administrativos y procesos contables." },
      {
        question: "¿InnoAdmin se integra con facturación electrónica?",
        answer: "Sí. Puede conectarse con Inno e-CF para integrar la operación administrativa con la emisión y el seguimiento de comprobantes fiscales electrónicos.",
        answerHtml: "Sí. Puede conectarse con <a href=\"/innoecf\" data-route>Inno e-CF</a> para integrar la operación administrativa con la emisión y el seguimiento de comprobantes fiscales electrónicos.",
      },
      { question: "¿InnoAdmin funciona desde la web?", answer: "Sí. InnoAdmin se ofrece como una aplicación web para centralizar la información de la empresa en un mismo entorno." },
    ],
  },
  {
    slug: "innoproject",
    name: "InnoProject",
    category: "Proyectos y obras",
    shortDescription: "Controla proyectos, costos, documentos y avance en un solo lugar.",
    description: "Plataforma para gestionar proyectos, presupuestos, incidencias, documentos, bitácoras, gastos, costos, cubicaciones y valorizaciones.",
    problems: [
      "Seguimiento de proyectos en archivos separados",
      "Desviaciones de presupuesto difíciles de detectar",
      "Documentos, incidencias y avances sin trazabilidad",
    ],
    features: ["Proyectos y presupuestos de ingeniería", "Incidencias y bitácoras de obra", "Documentación técnica", "Gastos y costos operativos", "Cubicaciones", "Valorizaciones de subcontratistas"],
    image: "/assets/products/innoproject-dashboard.png",
    imageAlt: "Dashboard de InnoProject con proyectos, presupuesto y avance",
    route: "/innoproject",
    legacyRoute: "/productos/innoproject",
    accent: "blue",
    seoDescription: "InnoProject es un software para gestionar proyectos y obras: presupuestos, costos, bitácoras, incidencias, documentos, cubicaciones y valorizaciones.",
    seoTitle: "InnoProject | Software para gestión de obras",
    directAnswer: "InnoProject es un software de gestión de proyectos y obras que organiza presupuestos, costos, bitácoras, incidencias, documentos, cubicaciones y valorizaciones.",
    applicationSubCategory: "Software de construcción y gestión de obras (ConTech)",
    operatingSystems: ["Web", "Android", "iOS"],
    idealFor: "Constructoras, ingenierías y equipos que necesitan trazabilidad sobre costos, avances y documentación de obra.",
    faq: [
      { question: "¿Qué controla InnoProject en una obra?", answer: "Permite dar seguimiento a presupuestos, costos, gastos, documentos, incidencias, bitácoras, cubicaciones y valorizaciones de proyectos." },
      { question: "¿InnoProject ayuda a gestionar cubicaciones?", answer: "Sí. Incluye la gestión de cubicaciones y valorizaciones para mantener trazabilidad sobre avances y costos de obra." },
      {
        question: "¿InnoProject puede integrarse con facturación electrónica?",
        answer: "Sí. Las cubicaciones y los avances de obra gestionados en InnoProject pueden relacionarse con la facturación electrónica mediante la integración con Inno e-CF, orientada a la operación de e-CF ante la DGII.",
        answerHtml: "Sí. Las <strong>cubicaciones</strong> y los avances de obra gestionados en InnoProject pueden relacionarse con la facturación electrónica mediante la integración con <a href=\"/innoecf\" data-route>Inno e-CF</a>, orientada a la operación de e-CF ante la <abbr title=\"Dirección General de Impuestos Internos\">DGII</abbr>.",
      },
    ],
  },
  {
    slug: "innoecf",
    name: "Inno e-CF",
    category: "Facturación electrónica",
    shortDescription: "Gestiona la facturación electrónica y su integración con tu operación.",
    description: "Solución de facturación electrónica para República Dominicana, orientada a la emisión y trazabilidad de e-CF conforme al marco de la Ley 32-23, y a la integración con Odoo, InnoAdmin e InnoProject.",
    problems: [
      "Procesos de emisión electrónica desconectados",
      "Seguimiento manual de estados y comprobantes",
      "Integraciones administrativas incompletas",
    ],
    features: ["Emisión de comprobantes e-CF", "Consulta de estados ante la DGII", "Trazabilidad de documentos y acuses", "Integración con Odoo", "Integración con InnoAdmin", "Integración con InnoProject"],
    image: "/assets/products/innoecf-dashboard.png",
    imageAlt: "Dashboard de Inno e-CF con comprobantes y estados de facturación electrónica",
    route: "/innoecf",
    legacyRoute: "/productos/innoecf",
    accent: "indigo",
    seoDescription: "Inno e-CF gestiona la emisión, consulta y trazabilidad de comprobantes e-CF en República Dominicana, conforme al marco de la Ley 32-23.",
    seoTitle: "Inno e-CF | Facturación electrónica en RD",
    directAnswer: "Inno e-CF es una solución de facturación electrónica para República Dominicana que permite emitir, consultar y dar trazabilidad a comprobantes fiscales electrónicos e-CF bajo el marco de la Ley 32-23 de Facturación Electrónica.",
    applicationSubCategory: "Software de facturación electrónica",
    operatingSystems: ["Web"],
    idealFor: "Empresas dominicanas que necesitan conectar la facturación electrónica con sus procesos administrativos.",
    faq: [
      { question: "¿Qué es Inno e-CF?", answer: "Es una solución de facturación electrónica para República Dominicana orientada a la emisión, consulta y trazabilidad de comprobantes fiscales electrónicos e-CF." },
      {
        question: "¿Qué normativa de facturación electrónica contempla Inno e-CF?",
        answer: "Inno e-CF está orientado a la operación de comprobantes fiscales electrónicos bajo el marco de la Ley 32-23 de Facturación Electrónica de la República Dominicana y los requisitos técnicos establecidos por la DGII.",
        answerHtml: "Inno e-CF está orientado a la operación de comprobantes fiscales electrónicos bajo el marco de la <a href=\"https://dgii.gov.do/transparencia/baseLegal/Documents/Leyes/Ley%2032-23.pdf\" target=\"_blank\" rel=\"noopener noreferrer\">Ley 32-23 de Facturación Electrónica de la República Dominicana</a> y los requisitos técnicos establecidos por la <abbr title=\"Dirección General de Impuestos Internos\">DGII</abbr>.",
      },
      { question: "¿Inno e-CF se integra con la DGII?", answer: "La solución está orientada a la emisión de e-CF ante la DGII y al seguimiento de los estados de los comprobantes electrónicos." },
      {
        question: "¿Con qué sistemas se integra Inno e-CF?",
        answer: "Inno e-CF puede integrarse con Odoo, InnoAdmin e InnoProject para conectar la facturación electrónica con la operación administrativa y la gestión de proyectos.",
        answerHtml: "Inno e-CF puede integrarse con Odoo, <a href=\"/innoadmin\" data-route>InnoAdmin</a> e <a href=\"/innoproject\" data-route>InnoProject</a> para conectar la facturación electrónica con la operación administrativa y la gestión de proyectos.",
      },
    ],
  },
  {
    slug: "innocapture",
    name: "InnoCapture",
    category: "Captura inteligente",
    shortDescription: "Convierte documentos y mensajes en información lista para procesar.",
    description: "Solución para capturar y procesar gastos, comprobantes, incidencias y otros datos mediante OCR, Telegram, WhatsApp y automatizaciones.",
    problems: [
      "Digitación repetitiva de comprobantes y gastos",
      "Información recibida por múltiples canales",
      "Datos sin estructura para continuar procesos",
    ],
    features: ["Captura mediante OCR", "Recepción por Telegram", "Flujos por WhatsApp", "Procesamiento de gastos", "Registro de incidencias", "Automatizaciones"],
    image: "/assets/products/capturebot-mock.svg",
    imageAlt: "Vista conceptual de InnoCapture para captura automatizada de documentos",
    route: "/innocapture",
    legacyRoute: "/productos/innocapture",
    accent: "cyan",
    seoDescription: "InnoCapture convierte gastos, comprobantes e incidencias en datos procesables mediante OCR, Telegram, WhatsApp y automatizaciones.",
    seoTitle: "InnoCapture | Captura inteligente con OCR",
    directAnswer: "InnoCapture es una solución de captura inteligente que convierte documentos y mensajes en datos procesables mediante OCR, Telegram, WhatsApp y automatizaciones.",
    applicationSubCategory: "Captura inteligente y automatización documental",
    operatingSystems: ["Web"],
    idealFor: "Equipos que reciben comprobantes, gastos o incidencias por varios canales y necesitan reducir la digitación manual.",
    faq: [
      { question: "¿Qué información procesa InnoCapture?", answer: "Procesa gastos, comprobantes, incidencias y otros datos recibidos como documentos o mensajes." },
      { question: "¿InnoCapture utiliza OCR?", answer: "Sí. Utiliza reconocimiento óptico de caracteres para extraer información de documentos y convertirla en datos procesables." },
      { question: "¿Puede recibir información desde mensajería?", answer: "Sí. Admite flujos de captura mediante Telegram y WhatsApp, además de automatizaciones para continuar el proceso." },
    ],
  },
];

/** @param {string} slug */
export const getProductBySlug = (slug) => products.find((product) => product.slug === slug);
